import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type {
  BudgetAnnualBill,
  BudgetGroup,
  BudgetGroupKind,
  BudgetSnapshot,
} from '@almanac/shared';
import { LoadStatus } from '../components/LoadStatus.js';
import {
  type TrendPoint,
  addBudgetGroup,
  addBudgetItem,
  applyBalancerCuts,
  balancerPlan,
  billMonthly,
  billsForGroup,
  carryForward,
  computeTotals,
  fmtAmount,
  fmtDiff,
  fmtMoney,
  groupTotals,
  monthKeyFor,
  monthLabel,
  monthName,
  moveBudgetGroupToIndex,
  moveBudgetItemToGroup,
  projectedBalance,
  removeBudgetBill,
  removeBudgetGroup,
  removeBudgetItem,
  reorderBudgetItems,
  seedSnapshotFor,
  shiftMonthKey,
  trendSeries,
  updateBudgetGroup,
  updateBudgetItem,
  upsertBudgetBill,
  useBudgetMonths,
  useSaveBudgetMonth,
} from '../lib/budget.js';
import { useCurrentUser } from '../lib/me.js';

/**
 * Budget — the broad monthly plan: planned vs actual per line, groups
 * the user shapes themselves, annual bills provisioned at ÷12. Grown
 * out of the standalone tracker Davo built; MoneyWiz keeps day-to-day
 * transactions, this page keeps the plan (PROJECT.md §Budget).
 *
 * Strictly personal: each signed-in user sees and edits only their own
 * figures. The interaction model is the old tracker's best part, kept:
 * tap a number, type, apply. Everything else — labels, groups, bills —
 * edits through small sheets on the same drawer chassis as the rest of
 * the app.
 */
type Sheet =
  | { kind: 'value'; groupId: string; itemId: string; field: 'planned' | 'actual' }
  | { kind: 'item'; groupId: string; itemId: string | 'new' }
  | { kind: 'group'; groupId: string | 'new' }
  | { kind: 'bill'; billId: string | 'new' }
  | { kind: 'balancer' }
  | null;

export function BudgetPage() {
  const navigate = useNavigate();
  const months = useBudgetMonths();
  const save = useSaveBudgetMonth();
  const me = useCurrentUser();
  const currency = me.data?.currency ?? null;

  const [monthKey, setMonthKey] = useState(() => monthKeyFor(new Date()));
  const [sheet, setSheet] = useState<Sheet>(null);

  const monthList = useMemo(() => months.data ?? [], [months.data]);
  const current = monthList.find((m) => m.month === monthKey);
  const snapshot = current?.data ?? null;
  // Nearest earlier saved month — the carry-forward source.
  const previous = useMemo(
    () => [...monthList].reverse().find((m) => m.month < monthKey),
    [monthList, monthKey],
  );

  const totals = useMemo(() => (snapshot ? computeTotals(snapshot) : null), [snapshot]);
  // Where the month is heading once actuals diverge from the plan —
  // null while they agree, so the banner stays quiet.
  const projected = useMemo(() => {
    if (!snapshot || !totals) return null;
    const p = projectedBalance(snapshot);
    return Math.round(p) !== Math.round(totals.balancePlanned) ? p : null;
  }, [snapshot, totals]);
  // The proportional-cut proposal for an overcommitted month; null
  // while the plan balances, so the strip (and sheet) simply vanish
  // the moment the month is right.
  const balancer = useMemo(() => (snapshot ? balancerPlan(snapshot) : null), [snapshot]);
  // Planned totals across the last six saved months up to the one
  // being viewed — empty below two months (one dot isn't a trend).
  const trends = useMemo(() => trendSeries(monthList, monthKey), [monthList, monthKey]);

  function commit(next: BudgetSnapshot) {
    save.mutate({ month: monthKey, data: next });
  }

  const largestGroup = useMemo(() => {
    if (!snapshot) return null;
    let best: { title: string; planned: number } | null = null;
    for (const g of snapshot.groups) {
      if (g.kind === 'income') continue;
      const t = groupTotals(snapshot, g);
      if (!best || t.planned > best.planned) best = { title: g.title, planned: t.planned };
    }
    return best;
  }, [snapshot]);

  const whereItGoes = useMemo(() => {
    if (!snapshot) return [];
    return snapshot.groups
      .filter((g) => g.kind !== 'income')
      .map((g) => ({ group: g, planned: groupTotals(snapshot, g).planned }))
      .filter((e) => e.planned > 0)
      .sort((a, b) => b.planned - a.planned)
      .slice(0, 5);
  }, [snapshot]);

  return (
    <section>
      <header className="page-header">
        <div className="page-header__lead">
          <button
            type="button"
            className="page-header__icon-button page-header__back"
            aria-label="Back to home"
            onClick={() => navigate('/')}
          >
            <BackIcon />
          </button>
          <div>
            <div className="section__label">Your budget</div>
            <h2 className="page-header__title">Budget</h2>
          </div>
        </div>
      </header>

      <LoadStatus isLoading={months.isLoading} isError={months.isError} error={months.error} />

      <div className="page-columns">
        <div className="page-measure">
          <div className="budget-month">
            <button
              type="button"
              className="budget-month__nav"
              aria-label="Previous month"
              onClick={() => setMonthKey((k) => shiftMonthKey(k, -1))}
            >
              ‹
            </button>
            <span className="budget-month__label">{monthLabel(monthKey)}</span>
            <button
              type="button"
              className="budget-month__nav"
              aria-label="Next month"
              onClick={() => setMonthKey((k) => shiftMonthKey(k, 1))}
            >
              ›
            </button>
          </div>

          {months.isSuccess && !snapshot && (
            <div className="budget-setup">
              {previous ? (
                <>
                  <p className="budget-setup__text">
                    No budget yet for {monthName(monthKey)}. Start from{' '}
                    {monthName(previous.month)}&rsquo;s plan? Planned amounts carry forward;
                    actuals begin empty.
                  </p>
                  <div className="budget-setup__actions">
                    <button
                      type="button"
                      className="btn btn--secondary btn--small"
                      onClick={() => commit(seedSnapshotFor(me.data?.email))}
                    >
                      Start fresh
                    </button>
                    <button
                      type="button"
                      className="btn btn--primary btn--small"
                      onClick={() => commit(carryForward(previous.data))}
                    >
                      Carry {monthName(previous.month)} forward
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p className="budget-setup__text">
                    Plan the month in broad strokes — income, spending groups, savings —
                    without tracking every transaction. Start with a ready-made set of
                    groups and shape it from there.
                  </p>
                  <div className="budget-setup__actions">
                    <button
                      type="button"
                      className="btn btn--primary btn--small"
                      onClick={() => commit(seedSnapshotFor(me.data?.email))}
                    >
                      Start my budget
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {snapshot && totals && (
            <>
              <div className="budget-banner">
                <div className="budget-banner__cell">
                  <div className="budget-banner__label">Income</div>
                  <div className="budget-banner__value">{fmtAmount(totals.incomePlanned)}</div>
                </div>
                <div className="budget-banner__cell">
                  <div className="budget-banner__label">Committed</div>
                  <div className="budget-banner__value">{fmtAmount(totals.committedPlanned)}</div>
                </div>
                <div className="budget-banner__cell budget-banner__cell--balance">
                  <div className="budget-banner__label">Balance</div>
                  <div
                    className={`budget-banner__value ${
                      totals.balancePlanned >= 0
                        ? 'budget-banner__value--pos'
                        : 'budget-banner__value--neg'
                    }`}
                  >
                    {totals.balancePlanned >= 0 ? '+' : '−'}
                    {fmtAmount(Math.abs(totals.balancePlanned))}
                  </div>
                  {projected !== null && (
                    <div
                      className={`budget-banner__proj ${
                        projected >= 0 ? 'budget-banner__proj--pos' : 'budget-banner__proj--neg'
                      }`}
                    >
                      heading {projected >= 0 ? '+' : '−'}
                      {fmtAmount(Math.abs(projected))}
                    </div>
                  )}
                </div>
              </div>

              {balancer && (
                <button
                  type="button"
                  className="doc-attention"
                  onClick={() => setSheet({ kind: 'balancer' })}
                >
                  <WarnIcon />
                  {fmtMoney(balancer.shortfall, currency)} short of a balanced plan
                  <span className="doc-attention__go">Balance ›</span>
                </button>
              )}

              {snapshot.groups.map((group) =>
                group.kind === 'savings' ? (
                  <SavingsCard
                    key={group.id}
                    snapshot={snapshot}
                    group={group}
                    incomePlanned={totals.incomePlanned}
                    onOpenSheet={setSheet}
                  />
                ) : (
                  <GroupCard
                    key={group.id}
                    snapshot={snapshot}
                    group={group}
                    onOpenSheet={setSheet}
                  />
                ),
              )}

              <AnnualBillsCard snapshot={snapshot} onOpenSheet={setSheet} />

              {/* On phones the trends card sits after the plan (the
                  rail below is display:none there); on desktop the
                  rail copy shows instead. */}
              {trends.length > 0 && (
                <div className="mobile-only">
                  <TrendsCard trends={trends} />
                </div>
              )}

              <button
                type="button"
                className="documents-add"
                onClick={() => setSheet({ kind: 'group', groupId: 'new' })}
              >
                <span className="documents-add__plus" aria-hidden>
                  +
                </span>
                Add a group
              </button>
            </>
          )}
        </div>

        {/* Desktop-only rail (display:none below 840px). */}
        <aside className="page-rail">
          {snapshot && totals && (
            <>
              {trends.length > 0 && <TrendsCard trends={trends} />}

              <div className="rail-card">
                <div className="doc-seclabel">{monthName(monthKey)} at a glance</div>
                <div className="rail-glance">
                  <div className="rail-glance__line rail-glance__line--split">
                    Income <strong>{fmtMoney(totals.incomePlanned, currency)}</strong>
                  </div>
                  <div className="rail-glance__line rail-glance__line--split">
                    Committed <strong>{fmtMoney(totals.committedPlanned, currency)}</strong>
                  </div>
                  <div className="rail-glance__line rail-glance__line--split">
                    Balance{' '}
                    <strong
                      style={{
                        color: totals.balancePlanned >= 0 ? 'var(--positive)' : 'var(--accent)',
                      }}
                    >
                      {totals.balancePlanned >= 0 ? '+' : '−'}
                      {fmtMoney(Math.abs(totals.balancePlanned), currency)}
                    </strong>
                  </div>
                </div>
                <div className="budget-meter">
                  <div
                    className="budget-meter__fill"
                    style={{
                      width: `${Math.min(
                        100,
                        totals.incomePlanned > 0
                          ? (totals.committedPlanned / totals.incomePlanned) * 100
                          : 100,
                      )}%`,
                      background:
                        totals.balancePlanned >= 0 ? 'var(--positive)' : 'var(--accent)',
                    }}
                  />
                </div>
              </div>

              {whereItGoes.length > 0 && (
                <div className="rail-card">
                  <div className="doc-seclabel">Where it goes</div>
                  {whereItGoes.map(({ group, planned }, idx) => (
                    <div
                      key={group.id}
                      className="budget-grow"
                      style={idx === whereItGoes.length - 1 ? { paddingBottom: '0.7rem' } : undefined}
                    >
                      <span className="budget-grow__name">{group.title}</span>
                      <span className="budget-grow__track">
                        <span
                          className="budget-grow__fill"
                          style={{
                            width: `${Math.round((planned / (whereItGoes[0]?.planned || 1)) * 100)}%`,
                          }}
                        />
                      </span>
                      <span className="budget-grow__amt">{fmtAmount(planned)}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="rail-card">
                <div className="doc-seclabel">At a glance</div>
                <div className="rail-glance">
                  <div className="rail-glance__line">
                    <strong>{Math.round(totals.savingsRate * 100)}%</strong> savings rate
                  </div>
                  {snapshot.annualBills.length > 0 && (
                    <div className="rail-glance__line">
                      <strong>{snapshot.annualBills.length}</strong> annual{' '}
                      {snapshot.annualBills.length === 1 ? 'bill' : 'bills'} ·{' '}
                      {fmtMoney(totals.provisionsMonthly, currency)}/mo provisioned
                    </div>
                  )}
                  {largestGroup && (
                    <div className="rail-glance__line">
                      <strong>{largestGroup.title}</strong> is the largest group
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </aside>
      </div>

      {sheet && snapshot && (
        <BudgetSheet
          sheet={sheet}
          snapshot={snapshot}
          monthKey={monthKey}
          currency={currency}
          onCommit={commit}
          onClose={() => setSheet(null)}
        />
      )}
    </section>
  );
}

// ── Cards ──────────────────────────────────────────────────────────

const HEAD_COLS = (
  <div className="budget-cols budget-cols--head">
    <span />
    <span>Plan</span>
    <span>Actual</span>
    <span>Diff</span>
  </div>
);

function num(value: number, onEdit?: () => void, ariaLabel?: string) {
  const zero = value === 0;
  const text = zero ? '–' : fmtAmount(value);
  if (!onEdit) {
    return (
      <span className={`budget-row__num ${zero ? 'budget-row__num--zero' : ''}`}>{text}</span>
    );
  }
  return (
    <button
      type="button"
      className={`budget-row__num budget-row__num--editable ${zero ? 'budget-row__num--zero' : ''}`}
      onClick={onEdit}
      aria-label={ariaLabel}
    >
      {text}
    </button>
  );
}

/** Diff cell. For income groups the polarity flips: earning more than
 *  planned is good news, so the classes swap while the sign stays. */
function diffCell(actual: number, planned: number, income: boolean) {
  const d = Math.round(actual - planned);
  const cls =
    d === 0
      ? 'budget-diff--zero'
      : (d > 0) !== income
        ? 'budget-diff--over'
        : 'budget-diff--under';
  return <span className={`budget-row__num ${cls}`}>{fmtDiff(d)}</span>;
}

function GroupCard({
  snapshot,
  group,
  onOpenSheet,
}: {
  snapshot: BudgetSnapshot;
  group: BudgetGroup;
  onOpenSheet: (s: Sheet) => void;
}) {
  const t = groupTotals(snapshot, group);
  const bills = billsForGroup(snapshot, group.id);
  const income = group.kind === 'income';

  return (
    <div className="budget-card">
      <div className="budget-card__header">
        <button
          type="button"
          className="budget-card__title budget-card__title--button"
          onClick={() => onOpenSheet({ kind: 'group', groupId: group.id })}
        >
          {group.title}
        </button>
        <span className="budget-card__subtotal">
          {fmtAmount(t.planned)} / {fmtAmount(t.actual)}
        </span>
      </div>
      {HEAD_COLS}
      {group.items.map((item) => (
        <div key={item.id} className="budget-cols budget-row">
          <button
            type="button"
            className="budget-row__label budget-row__label--button"
            onClick={() => onOpenSheet({ kind: 'item', groupId: group.id, itemId: item.id })}
          >
            {item.label}
          </button>
          {num(
            item.planned,
            () => onOpenSheet({ kind: 'value', groupId: group.id, itemId: item.id, field: 'planned' }),
            `Edit plan for ${item.label}`,
          )}
          {num(
            item.actual,
            () => onOpenSheet({ kind: 'value', groupId: group.id, itemId: item.id, field: 'actual' }),
            `Edit actual for ${item.label}`,
          )}
          {diffCell(item.actual, item.planned, income)}
        </div>
      ))}
      {bills.map((bill) => (
        <div key={bill.id} className="budget-cols budget-row budget-row--provision">
          <button
            type="button"
            className="budget-row__label budget-row__label--button"
            onClick={() => onOpenSheet({ kind: 'bill', billId: bill.id })}
          >
            {bill.label} <small>(annual ÷12)</small>
          </button>
          {num(billMonthly(bill))}
          {num(billMonthly(bill))}
          <span className="budget-row__num budget-diff--zero">–</span>
        </div>
      ))}
      <div className="budget-cols budget-row budget-foot">
        <span className="budget-row__label">Subtotal</span>
        {num(t.planned)}
        {num(t.actual)}
        {diffCell(t.actual, t.planned, income)}
      </div>
      <button
        type="button"
        className="budget-addline"
        onClick={() => onOpenSheet({ kind: 'item', groupId: group.id, itemId: 'new' })}
      >
        + Add a line
      </button>
    </div>
  );
}

function SavingsCard({
  snapshot,
  group,
  incomePlanned,
  onOpenSheet,
}: {
  snapshot: BudgetSnapshot;
  group: BudgetGroup;
  incomePlanned: number;
  onOpenSheet: (s: Sheet) => void;
}) {
  const t = groupTotals(snapshot, group);
  const max = Math.max(...group.items.map((i) => i.planned), 1);
  const rate = incomePlanned > 0 ? Math.round((t.planned / incomePlanned) * 100) : 0;

  return (
    <div className="budget-card">
      <div className="budget-card__header">
        <button
          type="button"
          className="budget-card__title budget-card__title--button"
          onClick={() => onOpenSheet({ kind: 'group', groupId: group.id })}
        >
          {group.title}
        </button>
        <span className="budget-card__subtotal">
          {fmtAmount(t.planned)} · {rate}% of income
        </span>
      </div>
      {group.items.map((item) => (
        <div key={item.id} className="budget-srow">
          <button
            type="button"
            className="budget-srow__name budget-row__label--button"
            onClick={() => onOpenSheet({ kind: 'item', groupId: group.id, itemId: item.id })}
          >
            {item.label}
          </button>
          <span className="budget-srow__track">
            <span
              className="budget-srow__fill"
              style={{ width: `${Math.round((item.planned / max) * 100)}%` }}
            />
          </span>
          <button
            type="button"
            className="budget-srow__amt budget-row__num--editable"
            onClick={() =>
              onOpenSheet({ kind: 'value', groupId: group.id, itemId: item.id, field: 'planned' })
            }
            aria-label={`Edit plan for ${item.label}`}
          >
            {item.planned === 0 ? '–' : fmtAmount(item.planned)}
          </button>
        </div>
      ))}
      <button
        type="button"
        className="budget-addline"
        onClick={() => onOpenSheet({ kind: 'item', groupId: group.id, itemId: 'new' })}
      >
        + Add a line
      </button>
    </div>
  );
}

function AnnualBillsCard({
  snapshot,
  onOpenSheet,
}: {
  snapshot: BudgetSnapshot;
  onOpenSheet: (s: Sheet) => void;
}) {
  const bills = snapshot.annualBills;
  const totalAnnual = bills.reduce((sum, b) => sum + b.annualAmount, 0);
  const groupTitle = (id: string) => snapshot.groups.find((g) => g.id === id)?.title ?? '—';

  return (
    <div className="budget-card">
      <div className="budget-card__header">
        <span className="budget-card__title">Annual bills</span>
        <span className="budget-card__subtotal">provisioned monthly</span>
      </div>
      {bills.length > 0 && (
        <>
          <div className="budget-annual-strip">
            <div>
              <div className="l">Bills</div>
              <div className="v">{bills.length}</div>
            </div>
            <div>
              <div className="l">Total annual</div>
              <div className="v">{fmtAmount(totalAnnual)}</div>
            </div>
            <div>
              <div className="l">Monthly ÷12</div>
              <div className="v">{fmtAmount(totalAnnual / 12)}</div>
            </div>
          </div>
          <div className="budget-cols budget-cols--head">
            <span />
            <span>Annual</span>
            <span>÷12</span>
            <span>Group</span>
          </div>
          {bills.map((bill) => (
            <div key={bill.id} className="budget-cols budget-row">
              <button
                type="button"
                className="budget-row__label budget-row__label--button"
                onClick={() => onOpenSheet({ kind: 'bill', billId: bill.id })}
              >
                {bill.label}
              </button>
              {num(bill.annualAmount)}
              {num(billMonthly(bill))}
              <span className="budget-row__num budget-row__num--zero budget-row__num--group">
                {groupTitle(bill.groupId)}
              </span>
            </div>
          ))}
        </>
      )}
      {bills.length === 0 && (
        <p className="budget-card__empty">
          Yearly bills — insurance, registrations, passes — provisioned into their group at
          ÷12, so the month they land is never a surprise.
        </p>
      )}
      <button
        type="button"
        className="budget-addline"
        onClick={() => onOpenSheet({ kind: 'bill', billId: 'new' })}
      >
        + Add an annual bill
      </button>
    </div>
  );
}

// ── Sheets ─────────────────────────────────────────────────────────

function BudgetSheet({
  sheet,
  snapshot,
  monthKey,
  currency,
  onCommit,
  onClose,
}: {
  sheet: NonNullable<Sheet>;
  snapshot: BudgetSnapshot;
  monthKey: string;
  currency: string | null;
  onCommit: (next: BudgetSnapshot) => void;
  onClose: () => void;
}) {
  switch (sheet.kind) {
    case 'value':
      return (
        <ValueSheet
          sheet={sheet}
          snapshot={snapshot}
          monthKey={monthKey}
          currency={currency}
          onCommit={onCommit}
          onClose={onClose}
        />
      );
    case 'item':
      return <ItemSheet sheet={sheet} snapshot={snapshot} onCommit={onCommit} onClose={onClose} />;
    case 'group':
      return <GroupSheet sheet={sheet} snapshot={snapshot} onCommit={onCommit} onClose={onClose} />;
    case 'bill':
      return <BillSheet sheet={sheet} snapshot={snapshot} onCommit={onCommit} onClose={onClose} />;
    case 'balancer':
      return (
        <BalancerSheet
          snapshot={snapshot}
          monthKey={monthKey}
          currency={currency}
          onCommit={onCommit}
          onClose={onClose}
        />
      );
  }
}

/** The Balancer — a concrete proportional-cut proposal for an
 *  overcommitted month. Read-only until Apply: the sheet shows each
 *  flexible group's before → after and the resulting balance, and
 *  Apply rewrites the plan through the normal commit (each group's
 *  cut spreads across its lines the same way, whole units). Fixed
 *  groups, savings, and annual bills are never touched. */
function BalancerSheet({
  snapshot,
  monthKey,
  currency,
  onCommit,
  onClose,
}: {
  snapshot: BudgetSnapshot;
  monthKey: string;
  currency: string | null;
  onCommit: (next: BudgetSnapshot) => void;
  onClose: () => void;
}) {
  const plan = useMemo(() => balancerPlan(snapshot), [snapshot]);
  // The month can balance underneath an open sheet (an Apply just
  // landed, or an edit elsewhere) — close rather than showing a
  // proposal for a problem that no longer exists.
  useEffect(() => {
    if (!plan) onClose();
  }, [plan, onClose]);
  if (!plan) return null;

  const cuts = plan.cuts.filter((c) => c.cut > 0);
  const cutTotal = cuts.reduce((s, c) => s + c.cut, 0);
  const fixedTitles = snapshot.groups
    .filter((g) => (g.kind === 'expense' && g.fixed) || g.kind === 'savings')
    .map((g) => g.title);

  function apply() {
    if (!plan) return;
    onCommit(applyBalancerCuts(snapshot, plan));
    onClose();
  }

  return (
    <SheetShell
      onClose={onClose}
      title={`Balance ${monthName(monthKey)}`}
      label="Balance the month"
      footer={
        <>
          <span />
          <div className="drawer__footer-actions">
            <button type="button" className="btn btn--secondary btn--small" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn--primary btn--small"
              onClick={apply}
              disabled={cuts.length === 0}
            >
              Apply cuts
            </button>
          </div>
        </>
      }
    >
      {cuts.length === 0 ? (
        <p className="balancer-lead">
          The plan commits <strong>{fmtMoney(plan.shortfall, currency)}</strong> more than the
          month&rsquo;s income, but every spending group is marked Fixed — there&rsquo;s nothing
          to trim. Mark a group Flexible from its sheet to let the Balancer propose cuts.
        </p>
      ) : (
        <>
          <p className="balancer-lead">
            The plan commits <strong>{fmtMoney(plan.shortfall, currency)}</strong> more than the
            month&rsquo;s income. Trimming the flexible{' '}
            {cuts.length === 1 ? 'group' : 'groups'} in proportion{' '}
            {plan.feasible ? 'closes it:' : 'recovers what it can:'}
          </p>
          <div className="balancer-rows">
            {cuts.map((c) => (
              <div key={c.groupId} className="balancer-row">
                <span className="balancer-row__name">{c.title}</span>
                <span className="balancer-row__move">
                  {fmtAmount(c.before)} → {fmtAmount(c.after)}
                </span>
                <span className="balancer-row__cut">−{fmtAmount(c.cut)}</span>
              </div>
            ))}
          </div>
          <div className="balancer-after">
            {plan.feasible
              ? `Balance after: +${fmtAmount(cutTotal - plan.shortfall)} · savings untouched`
              : `Still ${fmtMoney(plan.shortfall - cutTotal, currency)} short after — the flexible groups only go so far`}
          </div>
          <p className="balancer-note">
            {fixedTitles.length > 0 ? (
              <>Fixed groups ({fixedTitles.join(', ')}) are left alone — mark any group Fixed or
              Flexible from its sheet. </>
            ) : (
              <>Mark a group Fixed from its sheet to keep the Balancer away from it. </>
            )}
            Cuts spread across each group&rsquo;s lines the same way; every line stays editable
            afterwards.
          </p>
        </>
      )}
    </SheetShell>
  );
}

/** Sparklines over the saved months' planned totals — income,
 *  committed (terracotta), savings rate (sage). The snapshots already
 *  hold every number; the card just draws them. */
function TrendsCard({ trends }: { trends: TrendPoint[] }) {
  const latest = trends[trends.length - 1]!;
  const first = trends[0]!;
  const committedDelta = Math.round(latest.committed - first.committed);
  const ratePp = Math.round((latest.savingsRate - first.savingsRate) * 100);
  const sinceLabel = monthName(first.month).split(' ')[0];
  return (
    <section className="budget-card budget-card--trends">
      <header className="budget-card__header">
        <h3 className="budget-card__title budget-card__title--plain">Trends</h3>
        <span className="budget-card__subtotal">last {trends.length} months</span>
      </header>
      <div className="trend-row">
        <div className="trend-row__label">
          <span className="trend-row__name">Income</span>
          <span className="trend-row__value">{fmtAmount(latest.income)}</span>
        </div>
        <Sparkline values={trends.map((p) => p.income)} stroke="var(--text-tertiary)" />
      </div>
      <div className="trend-row">
        <div className="trend-row__label">
          <span className="trend-row__name">Committed</span>
          <span className="trend-row__value">
            {fmtAmount(latest.committed)}
            {committedDelta !== 0 && (
              <span className="trend-row__delta">
                {' '}
                · {committedDelta > 0 ? '+' : '−'}
                {fmtAmount(Math.abs(committedDelta))} vs {sinceLabel}
              </span>
            )}
          </span>
        </div>
        <Sparkline values={trends.map((p) => p.committed)} stroke="var(--accent)" />
      </div>
      <div className="trend-row">
        <div className="trend-row__label">
          <span className="trend-row__name">Savings rate</span>
          <span className="trend-row__value">
            {Math.round(latest.savingsRate * 100)}%
            <span className="trend-row__delta">
              {' '}
              · {Math.abs(ratePp) < 2 ? 'steady' : `${ratePp > 0 ? '+' : '−'}${Math.abs(ratePp)}pp vs ${sinceLabel}`}
            </span>
          </span>
        </div>
        <Sparkline values={trends.map((p) => p.savingsRate)} stroke="var(--positive)" />
      </div>
    </section>
  );
}

function Sparkline({ values, stroke }: { values: number[]; stroke: string }) {
  const w = 96;
  const h = 26;
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  const range = hi - lo || 1;
  const points = values
    .map(
      (v, i) =>
        `${(3 + (i * (w - 6)) / (values.length - 1)).toFixed(1)},${(
          h - 4 - ((v - lo) / range) * (h - 8)
        ).toFixed(1)}`,
    )
    .join(' ');
  return (
    <svg className="trend-row__spark" width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden>
      <polyline
        points={points}
        fill="none"
        stroke={stroke}
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function WarnIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
      <path d="M12 9v4M12 17h.01" />
    </svg>
  );
}

function SheetShell({
  title,
  label,
  children,
  footer,
  onClose,
}: {
  title: string;
  label: string;
  children: React.ReactNode;
  footer: React.ReactNode;
  onClose: () => void;
}) {
  // Escape dismisses, matching every other drawer in the app (the
  // obligations/documents editors already do this).
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <>
      <div className="drawer-scrim" onClick={onClose} aria-hidden />
      <aside className="drawer" role="dialog" aria-modal="true" aria-label={label}>
        <div className="drawer__grip">
          <div className="drawer__handle" aria-hidden />
          <header className="drawer__header">
            <h2 className="drawer__title">{title}</h2>
          </header>
        </div>
        <div className="drawer__body">{children}</div>
        <footer className="drawer__footer">{footer}</footer>
      </aside>
    </>
  );
}

/** Parse a money input; '' and garbage become 0, negatives clamp. */
function parseAmount(raw: string): number {
  const v = Number(raw.replace(',', '.'));
  if (!Number.isFinite(v) || v < 0) return 0;
  return v;
}

function ValueSheet({
  sheet,
  snapshot,
  monthKey,
  currency,
  onCommit,
  onClose,
}: {
  sheet: Extract<NonNullable<Sheet>, { kind: 'value' }>;
  snapshot: BudgetSnapshot;
  monthKey: string;
  currency: string | null;
  onCommit: (next: BudgetSnapshot) => void;
  onClose: () => void;
}) {
  const group = snapshot.groups.find((g) => g.id === sheet.groupId);
  const item = group?.items.find((i) => i.id === sheet.itemId);
  const [raw, setRaw] = useState(() =>
    item && item[sheet.field] !== 0 ? String(item[sheet.field]) : '',
  );
  if (!group || !item) return null;

  const fieldLabel = sheet.field === 'planned' ? 'Plan' : 'Actual';
  const otherLabel = sheet.field === 'planned' ? 'Actual' : 'Plan';
  const otherValue = sheet.field === 'planned' ? item.actual : item.planned;

  function apply() {
    const value = parseAmount(raw);
    const patch = sheet.field === 'planned' ? { planned: value } : { actual: value };
    onCommit(updateBudgetItem(snapshot, sheet.groupId, sheet.itemId, patch));
    onClose();
  }

  return (
    <SheetShell
      onClose={onClose}
      title={`${item.label} — ${fieldLabel} · ${monthName(monthKey)}`}
      label={`Edit ${fieldLabel.toLowerCase()} for ${item.label}`}
      footer={
        <>
          <span />
          <div className="drawer__footer-actions">
            <button type="button" className="btn btn--secondary btn--small" onClick={onClose}>
              Cancel
            </button>
            <button type="button" className="btn btn--primary btn--small" onClick={apply}>
              Apply
            </button>
          </div>
        </>
      }
    >
      <label className="field">
        <span className="field__label">{fieldLabel}</span>
        <input
          className="field__input"
          type="number"
          inputMode="decimal"
          min={0}
          step="any"
          value={raw}
          placeholder="0"
          autoFocus
          onChange={(e) => setRaw(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') apply();
            if (e.key === 'Escape') onClose();
          }}
        />
        <span className="field__hint">
          {otherLabel}: {fmtMoney(otherValue, currency)}
        </span>
      </label>
    </SheetShell>
  );
}

function ItemSheet({
  sheet,
  snapshot,
  onCommit,
  onClose,
}: {
  sheet: Extract<NonNullable<Sheet>, { kind: 'item' }>;
  snapshot: BudgetSnapshot;
  onCommit: (next: BudgetSnapshot) => void;
  onClose: () => void;
}) {
  const group = snapshot.groups.find((g) => g.id === sheet.groupId);
  const isNew = sheet.itemId === 'new';
  const existing = isNew ? null : group?.items.find((i) => i.id === sheet.itemId);
  const [label, setLabel] = useState(existing?.label ?? '');
  const [planned, setPlanned] = useState(() =>
    existing && existing.planned !== 0 ? String(existing.planned) : '',
  );
  const [actual, setActual] = useState(() =>
    existing && existing.actual !== 0 ? String(existing.actual) : '',
  );
  // Which group the line should live in — editable, so a line can move
  // ("Car Park Rental belongs under Housing, actually") with its
  // figures intact.
  const [targetGroupId, setTargetGroupId] = useState(sheet.groupId);
  const [confirming, setConfirming] = useState(false);
  if (!group || (!isNew && !existing)) return null;

  const trimmed = label.trim();

  function saveItem() {
    if (!trimmed) return;
    const patch = { label: trimmed, planned: parseAmount(planned), actual: parseAmount(actual) };
    if (isNew) {
      onCommit(addBudgetItem(snapshot, targetGroupId, { id: crypto.randomUUID(), ...patch }));
    } else {
      // Patch in place first, then relocate — the move helper carries
      // the freshly-patched item across.
      let next = updateBudgetItem(snapshot, sheet.groupId, sheet.itemId, patch);
      next = moveBudgetItemToGroup(next, sheet.groupId, sheet.itemId, targetGroupId);
      onCommit(next);
    }
    onClose();
  }

  function remove() {
    onCommit(removeBudgetItem(snapshot, sheet.groupId, sheet.itemId));
    onClose();
  }

  return (
    <SheetShell
      onClose={onClose}
      title={isNew ? `Add a line · ${group.title}` : existing!.label}
      label={isNew ? 'Add a budget line' : `Edit ${existing!.label}`}
      footer={
        confirming && existing ? (
          <div className="drawer__confirm" role="alertdialog" aria-label="Confirm delete">
            <span className="drawer__confirm-message">
              Delete {existing.label}?
              <span className="drawer__confirm-detail">
                The line and its figures are removed from this month.
              </span>
            </span>
            <div className="drawer__confirm-actions">
              <button
                type="button"
                className="btn btn--secondary btn--small"
                onClick={() => setConfirming(false)}
                autoFocus
              >
                Keep
              </button>
              <button type="button" className="btn btn--primary btn--small" onClick={remove}>
                Delete
              </button>
            </div>
          </div>
        ) : (
          <>
            {existing ? (
              <button
                type="button"
                className="btn btn--ghost btn--small"
                onClick={() => setConfirming(true)}
              >
                Delete
              </button>
            ) : (
              <span />
            )}
            <div className="drawer__footer-actions">
              <button type="button" className="btn btn--secondary btn--small" onClick={onClose}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn--primary btn--small"
                onClick={saveItem}
                disabled={!trimmed}
              >
                Save
              </button>
            </div>
          </>
        )
      }
    >
      <label className="field">
        <span className="field__label">Label</span>
        <input
          className="field__input"
          type="text"
          value={label}
          placeholder="e.g. Groceries"
          autoFocus={isNew}
          onChange={(e) => setLabel(e.target.value)}
        />
      </label>
      <div className="field-row">
        <label className="field">
          <span className="field__label">Plan</span>
          <input
            className="field__input"
            type="number"
            inputMode="decimal"
            min={0}
            step="any"
            value={planned}
            placeholder="0"
            onChange={(e) => setPlanned(e.target.value)}
          />
        </label>
        <label className="field">
          <span className="field__label">Actual</span>
          <input
            className="field__input"
            type="number"
            inputMode="decimal"
            min={0}
            step="any"
            value={actual}
            placeholder="0"
            onChange={(e) => setActual(e.target.value)}
          />
        </label>
      </div>
      <label className="field">
        <span className="field__label">Group</span>
        <select
          className="field__input field__input--select"
          value={targetGroupId}
          onChange={(e) => setTargetGroupId(e.target.value)}
        >
          {snapshot.groups.map((g) => (
            <option key={g.id} value={g.id}>
              {g.title}
            </option>
          ))}
        </select>
        <span className="field__hint">
          Moving to another group keeps this month&rsquo;s figures.
        </span>
      </label>
    </SheetShell>
  );
}

function GroupSheet({
  sheet,
  snapshot,
  onCommit,
  onClose,
}: {
  sheet: Extract<NonNullable<Sheet>, { kind: 'group' }>;
  snapshot: BudgetSnapshot;
  onCommit: (next: BudgetSnapshot) => void;
  onClose: () => void;
}) {
  const isNew = sheet.groupId === 'new';
  const existing = isNew ? null : snapshot.groups.find((g) => g.id === sheet.groupId);
  const [title, setTitle] = useState(existing?.title ?? '');
  const [kind, setKind] = useState<BudgetGroupKind>(existing?.kind ?? 'expense');
  // Expense-only: fixed groups are off-limits to the Balancer.
  const [fixedFlag, setFixedFlag] = useState(existing?.fixed ?? false);
  // Arranging is staged locally and applied on Save, like every other
  // field in the sheet — Cancel walks away from reorders too.
  const [lines, setLines] = useState(() => existing?.items ?? []);
  const [posIndex, setPosIndex] = useState(() =>
    snapshot.groups.findIndex((g) => g.id === sheet.groupId),
  );
  // The row just moved flashes sage briefly so the eye can follow it.
  const [movedId, setMovedId] = useState<string | null>(null);
  const movedTimer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(movedTimer.current), []);
  const [confirming, setConfirming] = useState(false);
  if (!isNew && !existing) return null;

  const trimmed = title.trim();
  const billCount = existing
    ? snapshot.annualBills.filter((b) => b.groupId === existing.id).length
    : 0;

  function flash(id: string) {
    setMovedId(id);
    window.clearTimeout(movedTimer.current);
    movedTimer.current = window.setTimeout(() => setMovedId(null), 900);
  }

  function moveLine(index: number, delta: -1 | 1) {
    const target = index + delta;
    if (target < 0 || target >= lines.length) return;
    const next = [...lines];
    const [line] = next.splice(index, 1);
    next.splice(target, 0, line!);
    setLines(next);
    flash(line!.id);
  }

  function sortLines(by: 'label' | 'planned') {
    setLines((current) =>
      [...current].sort((a, b) =>
        by === 'label' ? a.label.localeCompare(b.label) : b.planned - a.planned,
      ),
    );
  }

  function saveGroup() {
    if (!trimmed) return;
    const fixed = kind === 'expense' && fixedFlag ? true : undefined;
    if (isNew) {
      onCommit(
        addBudgetGroup(snapshot, {
          id: crypto.randomUUID(),
          title: trimmed,
          kind,
          fixed,
          items: [],
        }),
      );
    } else {
      let next = updateBudgetGroup(snapshot, sheet.groupId, { title: trimmed, kind, fixed });
      next = reorderBudgetItems(next, sheet.groupId, lines.map((i) => i.id));
      next = moveBudgetGroupToIndex(next, sheet.groupId, posIndex);
      onCommit(next);
    }
    onClose();
  }

  function remove() {
    onCommit(removeBudgetGroup(snapshot, sheet.groupId));
    onClose();
  }

  return (
    <SheetShell
      onClose={onClose}
      title={isNew ? 'Add a group' : existing!.title}
      label={isNew ? 'Add a budget group' : `Edit ${existing!.title}`}
      footer={
        confirming && existing ? (
          <div className="drawer__confirm" role="alertdialog" aria-label="Confirm delete">
            <span className="drawer__confirm-message">
              Delete {existing.title}?
              <span className="drawer__confirm-detail">
                Its {existing.items.length} {existing.items.length === 1 ? 'line' : 'lines'}
                {billCount > 0
                  ? ` and ${billCount} annual ${billCount === 1 ? 'bill' : 'bills'}`
                  : ''}{' '}
                are removed from this month.
              </span>
            </span>
            <div className="drawer__confirm-actions">
              <button
                type="button"
                className="btn btn--secondary btn--small"
                onClick={() => setConfirming(false)}
                autoFocus
              >
                Keep
              </button>
              <button type="button" className="btn btn--primary btn--small" onClick={remove}>
                Delete
              </button>
            </div>
          </div>
        ) : (
          <>
            {existing ? (
              <button
                type="button"
                className="btn btn--ghost btn--small"
                onClick={() => setConfirming(true)}
              >
                Delete
              </button>
            ) : (
              <span />
            )}
            <div className="drawer__footer-actions">
              <button type="button" className="btn btn--secondary btn--small" onClick={onClose}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn--primary btn--small"
                onClick={saveGroup}
                disabled={!trimmed}
              >
                Save
              </button>
            </div>
          </>
        )
      }
    >
      <label className="field">
        <span className="field__label">Title</span>
        <input
          className="field__input"
          type="text"
          value={title}
          placeholder="e.g. Pets"
          autoFocus={isNew}
          onChange={(e) => setTitle(e.target.value)}
        />
      </label>
      <label className="field">
        <span className="field__label">Counts as</span>
        <select
          className="field__input field__input--select"
          value={kind}
          onChange={(e) => setKind(e.target.value as BudgetGroupKind)}
        >
          <option value="expense">Spending</option>
          <option value="income">Income</option>
          <option value="savings">Savings</option>
        </select>
        <span className="field__hint">
          Income adds to the month; spending and savings both count as committed.
        </span>
      </label>

      {kind === 'expense' && (
        <label className="field">
          <span className="field__label">When the month overcommits</span>
          <select
            className="field__input field__input--select"
            value={fixedFlag ? 'fixed' : 'flexible'}
            onChange={(e) => setFixedFlag(e.target.value === 'fixed')}
          >
            <option value="flexible">Flexible — the Balancer may trim it</option>
            <option value="fixed">Fixed — never touched</option>
          </select>
          <span className="field__hint">
            The Balancer spreads cuts across flexible groups only; rent-like costs belong
            on Fixed. Savings are never touched either way.
          </span>
        </label>
      )}

      {existing && lines.length > 0 && (
        <div className="budget-lines-wrap">
          <div className="budget-lines-head">
            <span className="field__label">Lines</span>
            <span className="budget-sort">
              <button type="button" className="budget-sort__chip" onClick={() => sortLines('label')}>
                Sort A–Z
              </button>
              <button
                type="button"
                className="budget-sort__chip"
                onClick={() => sortLines('planned')}
              >
                By plan
              </button>
            </span>
          </div>
          <div className="budget-lines">
            {lines.map((item, idx) => (
              <div
                key={item.id}
                className={`budget-line ${movedId === item.id ? 'budget-line--moved' : ''}`}
              >
                <span className="budget-line__name">{item.label}</span>
                <span className="budget-line__amt">
                  {item.planned === 0 ? '–' : fmtAmount(item.planned)}
                </span>
                <span className="budget-line__moves">
                  <button
                    type="button"
                    className="budget-line__move"
                    disabled={idx === 0}
                    onClick={() => moveLine(idx, -1)}
                    aria-label={`Move ${item.label} up`}
                  >
                    <ChevronUpIcon />
                  </button>
                  <button
                    type="button"
                    className="budget-line__move"
                    disabled={idx === lines.length - 1}
                    onClick={() => moveLine(idx, 1)}
                    aria-label={`Move ${item.label} down`}
                  >
                    <ChevronDownIcon />
                  </button>
                </span>
              </div>
            ))}
          </div>
          <span className="field__hint">
            Order here is the order on the card. Annual-bill provisions always sit below the
            lines.
          </span>
        </div>
      )}

      {existing && snapshot.groups.length > 1 && (
        <div className="budget-lines-wrap">
          <span className="field__label budget-position__label">Position</span>
          <div className="budget-position">
            <span className="budget-position__text">
              <strong>{ordinal(posIndex + 1)}</strong> of {snapshot.groups.length} groups
            </span>
            <span className="budget-line__moves">
              <button
                type="button"
                className="budget-line__move"
                disabled={posIndex === 0}
                onClick={() => setPosIndex((i) => Math.max(0, i - 1))}
                aria-label="Move group up the page"
              >
                <ChevronUpIcon />
              </button>
              <button
                type="button"
                className="budget-line__move"
                disabled={posIndex === snapshot.groups.length - 1}
                onClick={() => setPosIndex((i) => Math.min(snapshot.groups.length - 1, i + 1))}
                aria-label="Move group down the page"
              >
                <ChevronDownIcon />
              </button>
            </span>
          </div>
        </div>
      )}
    </SheetShell>
  );
}

/** 1 → '1st', 2 → '2nd', 11 → '11th' — the group-position readout. */
function ordinal(n: number): string {
  const suffixes = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${suffixes[(v - 20) % 10] ?? suffixes[v] ?? 'th'}`;
}

function ChevronUpIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M6 14l6-6 6 6"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ChevronDownIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M6 10l6 6 6-6"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function BillSheet({
  sheet,
  snapshot,
  onCommit,
  onClose,
}: {
  sheet: Extract<NonNullable<Sheet>, { kind: 'bill' }>;
  snapshot: BudgetSnapshot;
  onCommit: (next: BudgetSnapshot) => void;
  onClose: () => void;
}) {
  const isNew = sheet.billId === 'new';
  const existing = isNew ? null : snapshot.annualBills.find((b) => b.id === sheet.billId);
  const expenseGroups = snapshot.groups.filter((g) => g.kind !== 'income');
  const [label, setLabel] = useState(existing?.label ?? '');
  const [annual, setAnnual] = useState(() =>
    existing && existing.annualAmount !== 0 ? String(existing.annualAmount) : '',
  );
  const [groupId, setGroupId] = useState(existing?.groupId ?? expenseGroups[0]?.id ?? '');
  const [confirming, setConfirming] = useState(false);
  if (!isNew && !existing) return null;

  const trimmed = label.trim();
  const monthly = parseAmount(annual) / 12;

  function saveBill() {
    if (!trimmed || !groupId) return;
    onCommit(
      upsertBudgetBill(snapshot, {
        id: existing?.id ?? crypto.randomUUID(),
        label: trimmed,
        annualAmount: parseAmount(annual),
        groupId,
      } satisfies BudgetAnnualBill),
    );
    onClose();
  }

  function remove() {
    onCommit(removeBudgetBill(snapshot, sheet.billId));
    onClose();
  }

  return (
    <SheetShell
      onClose={onClose}
      title={isNew ? 'Add an annual bill' : existing!.label}
      label={isNew ? 'Add an annual bill' : `Edit ${existing!.label}`}
      footer={
        confirming && existing ? (
          <div className="drawer__confirm" role="alertdialog" aria-label="Confirm delete">
            <span className="drawer__confirm-message">
              Delete {existing.label}?
              <span className="drawer__confirm-detail">
                Its ÷12 provision comes out of{' '}
                {snapshot.groups.find((g) => g.id === existing.groupId)?.title ?? 'its group'}.
              </span>
            </span>
            <div className="drawer__confirm-actions">
              <button
                type="button"
                className="btn btn--secondary btn--small"
                onClick={() => setConfirming(false)}
                autoFocus
              >
                Keep
              </button>
              <button type="button" className="btn btn--primary btn--small" onClick={remove}>
                Delete
              </button>
            </div>
          </div>
        ) : (
          <>
            {existing ? (
              <button
                type="button"
                className="btn btn--ghost btn--small"
                onClick={() => setConfirming(true)}
              >
                Delete
              </button>
            ) : (
              <span />
            )}
            <div className="drawer__footer-actions">
              <button type="button" className="btn btn--secondary btn--small" onClick={onClose}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn--primary btn--small"
                onClick={saveBill}
                disabled={!trimmed || !groupId}
              >
                Save
              </button>
            </div>
          </>
        )
      }
    >
      <label className="field">
        <span className="field__label">Bill</span>
        <input
          className="field__input"
          type="text"
          value={label}
          placeholder="e.g. Car Registration"
          autoFocus={isNew}
          onChange={(e) => setLabel(e.target.value)}
        />
      </label>
      <div className="field-row">
        <label className="field">
          <span className="field__label">Annual amount</span>
          <input
            className="field__input"
            type="number"
            inputMode="decimal"
            min={0}
            step="any"
            value={annual}
            placeholder="0"
            onChange={(e) => setAnnual(e.target.value)}
          />
        </label>
        <label className="field">
          <span className="field__label">Provisions into</span>
          <select
            className="field__input field__input--select"
            value={groupId}
            onChange={(e) => setGroupId(e.target.value)}
          >
            {expenseGroups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.title}
              </option>
            ))}
          </select>
        </label>
      </div>
      {monthly > 0 && (
        <p className="field__hint" style={{ marginTop: '-0.25rem' }}>
          Provisions {fmtAmount(monthly)}/month into the group&rsquo;s totals.
        </p>
      )}
    </SheetShell>
  );
}

function BackIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M15 18l-6-6 6-6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
